import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { DjangoService } from '@core/services/django.service';
import { PlausibleService } from '@core/services/plausible.service';
import { SessionService } from '@core/services/session.service';
import { AuthenticationResponse } from '@generated/openapi/model/authenticationResponse';
import { Login } from '@generated/openapi/model/login';
import { PasswordReset } from '@generated/openapi/model/passwordReset';
import { PasswordResetConfirm } from '@generated/openapi/model/passwordResetConfirm';
import { ProfilePictureUpload } from '@generated/openapi/model/profilePictureUpload';
import { PublicUser } from '@generated/openapi/model/publicUser';
import { Registration } from '@generated/openapi/model/registration';
import { UserDetails } from '@generated/openapi/model/userDetails';
import * as Sentry from '@sentry/angular';
import { concat, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

export enum UserGroups {
    salesPerson = 'Sales',
    tripManager = 'Trip Manager',
    paymentsAdmin = 'Payments Admin',
    bookingAdmin = 'Booking Admin',
    editor = 'Editor',
    preview = 'Preview',
    localLeader = 'Local Leader',
    impersonator = 'Impersonator',
}

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private djangoService = inject(DjangoService);
    private plausibleService = inject(PlausibleService);

    private sessionService = inject(SessionService);

    /**
     * The authenticated user, or null when there is no session. Every session
     * question, guards included, resolves through here.
     *
     * The cookies are HttpOnly, so only the server can answer this, and the
     * session tells us whether asking is worth a request. `fetch_user()` is
     * shared and cached for 30s, so the many subscribers cost one request
     * between them. An error maps to null instead of propagating, which would
     * terminate the stream for every subscriber.
     */
    private readonly currentUser: Observable<UserDetails | null> =
        this.sessionService.mayExist.pipe(
            switchMap(mayExist =>
                mayExist
                    ? this.fetch_user().pipe(catchError(() => of(null)))
                    : of(null)
            )
        );

    private readonly authenticationObservable = this.currentUser.pipe(
        map(user => user !== null)
    );

    register(data: Registration) {
        return this.djangoService
            .register(data)
            .pipe(map(data => this.handleLoginData(data)));
    }

    validateEmailExists({
        value,
    }: AbstractControl): Observable<ValidationErrors | null> {
        return timer(1000).pipe(
            switchMap(() => this.djangoService.emailExists(value)),
            map(() => ({ emailExists: true })),
            catchError((error: HttpErrorResponse) =>
                of(
                    error.status === HttpStatusCode.NotFound
                        ? null
                        : { emailCheckUnavailable: true }
                )
            )
        );
    }

    updateUser(data: Partial<UserDetails>) {
        this.djangoService.clear_cache();
        return this.djangoService.updateUser(data);
    }

    saveProfilePicture(imageData: string) {
        const data: ProfilePictureUpload = {
            profile_picture: imageData,
        };
        this.djangoService.clear_cache();
        return this.djangoService.uploadProfilePicture(data);
    }

    fetch_user(): Observable<UserDetails> {
        return this.djangoService.user().pipe(
            tap(user => {
                if (user.is_staff) {
                    this.plausibleService.disable();
                }
            }),
            tap(user => {
                // Sentry's user is global to the page, so an impersonated
                // client must not replace the staff member reporting errors.
                if (!user.impersonated_by) {
                    Sentry.setUser({ email: user.email });
                }
            })
        );
    }

    userVerified(): Observable<boolean> {
        return concat(
            of(true),
            this.fetch_user().pipe(map(user => user.verified))
        );
    }

    profile(referralCode: string): Observable<PublicUser> {
        return this.djangoService.profile(referralCode);
    }

    resendVerificationEmail() {
        return this.djangoService.resendVerificationEmail();
    }

    login(email: string, password: string) {
        const data: Login = {
            email: email,
            password: password,
        };
        return this.djangoService
            .login(data)
            .pipe(map(data => this.handleLoginData(data)));
    }

    logout() {
        return this.sessionService.logout();
    }

    public requestPasswordReset(email: string): Observable<any> {
        const data: PasswordReset = { email: email };
        return this.djangoService.requestPasswordReset(data);
    }

    public resetPassword(
        uid: string,
        token: string,
        password1: string,
        password2: string
    ) {
        const data: PasswordResetConfirm = {
            uid: uid,
            token: token,
            new_password1: password1,
            new_password2: password2,
        };
        return this.djangoService.confirmPasswordReset(data);
    }

    public isAuthenticated(): Observable<boolean> {
        return this.authenticationObservable.pipe(take(1));
    }

    public authenticationChanges(): Observable<boolean> {
        return this.authenticationObservable;
    }

    public isStaff(): Observable<boolean> {
        return this.currentUser.pipe(map(user => !!user?.is_staff));
    }

    public isVerified(): Observable<boolean> {
        return this.currentUser.pipe(map(user => !!user?.verified));
    }

    public hasGroup(group: string): Observable<boolean> {
        return this.currentUser.pipe(
            map(user => !!user?.groups.includes(group))
        );
    }

    public hasAnyGroup(...groups: UserGroups[]): Observable<UserGroups[]> {
        return this.currentUser.pipe(
            map(user => groups.filter(group => !!user?.groups.includes(group)))
        );
    }

    private handleLoginData(data: AuthenticationResponse) {
        this.sessionService.start(data.access_expiration);
        return data.user;
    }
}
